import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

interface PaginatorProps {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
}

/**
 * Build the page number array for Amazon-style pagination.
 * Always shows first page, last page, current page, and one neighbor on each side.
 * Gaps are represented as '...' entries.
 */
function getPageNumbers(current: number, total: number): Array<number | '...'> {
	if (total <= 7) {
		return Array.from({ length: total }, (_, i) => i + 1);
	}

	const pages = new Set<number>();
	pages.add(1);
	pages.add(total);

	for (let i = current - 1; i <= current + 1; i++) {
		if (i >= 1 && i <= total) {
			pages.add(i);
		}
	}

	const sorted = Array.from(pages).sort((a, b) => a - b);
	const result: Array<number | '...'> = [];

	for (let i = 0; i < sorted.length; i++) {
		if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
			result.push('...');
		}
		result.push(sorted[i]);
	}

	return result;
}

/**
 * Amazon-style paginator: Previous, numbered pages with ellipsis, Next.
 * Reusable across catalog, orders, and invoices.
 */
export function Paginator({
	currentPage,
	totalPages,
	onPageChange,
}: PaginatorProps): JSX.Element | null {
	const { t } = useTranslation();

	if (totalPages <= 1) return null;

	return (
		<nav
			className="flex items-center justify-center gap-1 mt-6"
			aria-label={t('Pagination')}
		>
			<button
				type="button"
				disabled={currentPage <= 1}
				onClick={() => onPageChange(currentPage - 1)}
				className="px-3 py-2 text-sm rounded-lg border border-brand-border text-strong hover:bg-sunken disabled:opacity-40 disabled:cursor-not-allowed min-h-touch"
			>
				{t('Previous')}
			</button>

			{getPageNumbers(currentPage, totalPages).map((page, idx) =>
				page === '...' ? (
					<span
						key={`ellipsis-${idx}`}
						className="px-2 py-2 text-sm text-brand-muted select-none"
					>
						{'...'}
					</span>
				) : (
					<button
						key={page}
						type="button"
						onClick={() => onPageChange(page as number)}
						className={`min-w-[40px] px-2 py-2 text-sm rounded-lg border min-h-touch transition-colors ${
							currentPage === page
								? 'bg-portal-accent border-portal-accent text-white font-medium'
								: 'border-brand-border text-strong hover:bg-sunken'
						}`}
					>
						{t(String(page))}
					</button>
				),
			)}

			<button
				type="button"
				disabled={currentPage >= totalPages}
				onClick={() => onPageChange(currentPage + 1)}
				className="px-3 py-2 text-sm rounded-lg border border-brand-border text-strong hover:bg-sunken disabled:opacity-40 disabled:cursor-not-allowed min-h-touch"
			>
				{t('Next')}
			</button>
		</nav>
	);
}
